import { CircleArrowDown, CircleArrowUp, X } from "lucide-react";
import {
  DropdownMenuCheckboxItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useThemesSearch } from "@/features/themes/hooks/useThemesSearch";
import { useState, memo } from "react";
import frIcon from "@/assets/fr-icon.svg";
import enIcon from "@/assets/en-icon.svg";

interface ThemeFilterGroupProps {
  themeFilter: string | null;
  onThemeChange: (themeId: string | null) => void;
}

export const ThemeFilterGroup = ({
  themeFilter,
  onThemeChange,
}: ThemeFilterGroupProps) => {
  const { t } = useTranslation();
  const { search, setSearch, themes } = useThemesSearch();
  const [themeOpen, setThemeOpen] = useState(false);

  const selectedTheme = themes.find((theme) => theme.id === themeFilter);
  const selectedThemeName = selectedTheme?.name ?? null;

  const handleSelectTheme = (id: string) => {
    onThemeChange(id);
    setSearch("");
    setThemeOpen(false);
  };

  const handleClearTheme = (e: React.MouseEvent) => {
    e.stopPropagation();
    onThemeChange(null);
    setSearch("");
  };

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>{t("keys.filter_by.topic")}</DropdownMenuLabel>
      <div className="relative px-2 pb-1">
        <div className="flex items-center rounded-sm border border-input bg-transparent text-sm focus-within:ring-1 focus-within:ring-ring">
          <input
            className="min-w-0 flex-1 bg-transparent px-2 py-1 outline-none placeholder:text-muted-foreground"
            placeholder={t("keys.search")}
            value={themeOpen ? search : (selectedThemeName ?? "")}
            onFocus={() => {
              setSearch("");
              setThemeOpen(true);
            }}
            onBlur={() => setThemeOpen(false)}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
          {themeFilter && (
            <button
              className="px-1.5 text-muted-foreground hover:text-foreground"
              onMouseDown={handleClearTheme}
              tabIndex={-1}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        {themeOpen && (
          <div className="absolute left-2 right-2 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
            {themes.length === 0 ? (
              <p className="px-2 py-2 text-center text-xs text-muted-foreground">
                {t("keys.table.no_results")}
              </p>
            ) : (
              themes.map((theme) => (
                <button
                  key={theme.id}
                  className="flex w-full items-center px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectTheme(theme.id)}
                >
                  {theme.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </DropdownMenuGroup>
  );
};

interface StatusFilterOption {
  value: string;
  label: string;
  variant?:
    | "secondary"
    | "dilemma_scheduled"
    | "dilemma_published"
    | "dilemma_archived"
    | "dilemma_submitted";
}

interface StatusFilterGroupProps {
  statusFilter: string | null;
  onStatusChange: (status: string | null) => void;
  options: StatusFilterOption[];
}

export const StatusFilterGroup = memo(function StatusFilterGroup({
  statusFilter,
  onStatusChange,
  options,
}: StatusFilterGroupProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>{t("keys.filter_by.status")}</DropdownMenuLabel>
      {options.map((option) => (
        <DropdownMenuCheckboxItem
          key={option.value}
          checked={statusFilter === option.value}
          onCheckedChange={() =>
            onStatusChange(statusFilter === option.value ? null : option.value)
          }
        >
          <Badge variant={option.variant || "secondary"}>{option.label}</Badge>
        </DropdownMenuCheckboxItem>
      ))}
    </DropdownMenuGroup>
  );
});

interface DateFilterGroupProps {
  ordering: string | null;
  onOrderingChange: (ordering: string | null) => void;
}

export const DateFilterGroup = memo(function DateFilterGroup({
  ordering,
  onOrderingChange,
}: DateFilterGroupProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>{t("keys.filter_by.date")}</DropdownMenuLabel>
      <DropdownMenuCheckboxItem
        checked={ordering === "created_at"}
        onCheckedChange={() =>
          onOrderingChange(ordering === "created_at" ? null : "created_at")
        }
      >
        <CircleArrowUp className="text-gray-500" />
        Asc
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={ordering === "-created_at"}
        onCheckedChange={() =>
          onOrderingChange(ordering === "-created_at" ? null : "-created_at")
        }
      >
        <CircleArrowDown className="text-gray-500" />
        Desc
      </DropdownMenuCheckboxItem>
    </DropdownMenuGroup>
  );
});

interface LanguageFilterGroupProps {
  languageFilter: "fr" | "en" | null;
  onLanguageChange: (language: "fr" | "en" | null) => void;
}

export const LanguageFilterGroup = memo(function LanguageFilterGroup({
  languageFilter,
  onLanguageChange,
}: LanguageFilterGroupProps) {
  const { t } = useTranslation();

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>
        {t("modules.dilemmas.keys.language")}
      </DropdownMenuLabel>
      <DropdownMenuCheckboxItem
        checked={languageFilter === "fr"}
        onCheckedChange={() =>
          onLanguageChange(languageFilter === "fr" ? null : "fr")
        }
      >
        <img src={frIcon} alt="FR" className="size-5" />
        {t("languages.fr")}
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={languageFilter === "en"}
        onCheckedChange={() =>
          onLanguageChange(languageFilter === "en" ? null : "en")
        }
      >
        <img src={enIcon} alt="EN" className="size-5" />
        {t("languages.en")}
      </DropdownMenuCheckboxItem>
    </DropdownMenuGroup>
  );
});
